import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CookiesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold font-['Unbounded'] mb-2">Cookie Policy</h1>
          <p className="text-gray-500 mb-12">Last updated: April 2026</p>

          <div className="space-y-8 text-gray-300 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">1. What Are Cookies</h2>
              <p>
                Cookies are small text files stored on your device when you visit a website. They help 
                websites remember your preferences and understand how you use the site. We use cookies 
                to provide a better experience and track campaign performance.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">2. How We Use Cookies</h2>
              <p className="mb-4">We use cookies for the following purposes:</p>
              
              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">Essential Cookies</h3>
                  <p className="text-gray-400 text-sm">
                    Required for the platform to function. These handle authentication, security, 
                    and basic functionality. You cannot opt out of these cookies.
                  </p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">Analytics Cookies</h3>
                  <p className="text-gray-400 text-sm">
                    Help us understand how users interact with the platform. We use this data to 
                    improve our services. These cookies collect anonymous usage data.
                  </p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">Campaign Tracking Cookies</h3>
                  <p className="text-gray-400 text-sm">
                    Essential for our performance-based model. These cookies track clicks, leads, 
                    and conversions from creator campaigns. They help attribute results to specific 
                    creators for accurate payouts.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">3. Campaign Tracking</h2>
              <p className="mb-4">
                Our campaign tracking system uses cookies and unique tracking links to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Track clicks from creator content to business offers</li>
                <li>Identify when a click leads to a signup or purchase</li>
                <li>Attribute conversions to the correct creator</li>
                <li>Calculate creator earnings based on performance</li>
              </ul>
              <p className="mt-4">
                This tracking is fundamental to how the platform works and ensures creators 
                are paid fairly for the results they deliver.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">4. Third-Party Cookies</h2>
              <p className="mb-4">We may use cookies from the following third parties:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li><strong>Stripe:</strong> For secure payment processing</li>
                <li><strong>Google:</strong> For authentication (Google Sign-In)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">5. Managing Cookies</h2>
              <p className="mb-4">
                You can control cookies through your browser settings. However, please note:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Disabling essential cookies will prevent the platform from working</li>
                <li>Disabling tracking cookies may affect campaign attribution</li>
                <li>Some features may not work correctly without cookies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">6. Cookie Retention</h2>
              <p>
                Session cookies are deleted when you close your browser. Persistent cookies remain 
                for varying periods depending on their purpose. Campaign tracking cookies typically 
                last for 30 days to ensure proper conversion attribution.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">7. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time. Changes will be posted on this 
                page with an updated revision date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">8. Contact Us</h2>
              <p>
                If you have questions about our use of cookies, contact us at privacy@elektrouniversal.dk
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-4xl mx-auto text-center text-sm text-gray-600">
          © 2026 EleKtro Universal. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default CookiesPage;
